<template>
  <div v-bind="$attrs" ref="containerRef">
    <slot name="activator" :attrs="attrs"></slot>
  </div>
  <Teleport to="body">
    <fade-transition :duration="300">
      <div v-if="open" class="menu" :style="menuStyle" ref="menuRef" @click="onClickItem" @keyup.enter="onClickItem">
        <slot/>
      </div>
    </fade-transition>
  </Teleport>
</template>

<script lang="ts" setup>

import {
  computed,
  CSSProperties,
  defineEmits,
  defineProps,
  onUnmounted,
  reactive,
  ref,
  watch
} from 'vue';
import { onClickOutside } from '@vueuse/core';
import useWindowResize from '@/composable/ui/useWindowResize';
import useWindowScroll from '@/composable/ui/useWindowScroll';
import FadeTransition from "@/components/Transitions/FadeTransition.vue";

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  activator: {
    type: Object as () => HTMLElement,
    required: false,
  },
  position: {
    type: String as () => 'top' | 'bottom' | 'left' | 'right',
    required: false,
    default: 'top',
  },
  width: {
    type: [Number],
  },
  closeOnSelect: {
    type: Boolean,
    default: () => true,
  },
  hiddenScroll: {
    type: Boolean,
    default: () => true,
    required: false,
  },
});

const emit = defineEmits(['update:open', 'clickOutside']);

const containerRef = ref<HTMLElement>();
const menuRef = ref<HTMLElement>();

const open = ref(false);
const location = reactive({
  top: 0,
  left: 0,
  width: 0,
});

const menuHeight = ref(0);

onClickOutside(menuRef, () => {
  if (open.value) {
    open.value = false;
    emit('clickOutside');
  }
});

const activatorRef = computed(() => props.activator || containerRef.value);

const menuStyle = computed((): CSSProperties => ({
    top: `${location.top + 3}px`,
    left: `${location.left + 1}px`,
    width: `${location.width - 2}px`,
}));

function onResize() {

  if (!activatorRef.value) return;
  const containerRect = activatorRef.value.getBoundingClientRect();

  let top = containerRect.top + window.scrollY;
  let left = containerRect.left + window.scrollX;

  const { width, height } = containerRect;

  if (props.position === 'bottom') top += height;
  else if (props.position === 'left') left -= width;
  else if (props.position === 'right') left += width;
  else if (props.position === 'top') {
    top -= 3;
    left -= 1;
  }

  let limitPosition = containerRect.bottom + menuHeight.value;
  if (props.position === 'top'){
    limitPosition -= height;
  }

  if((limitPosition) > window.innerHeight) {
    top = top - menuHeight.value - 6;
    if (props.position === 'bottom') top -= height;
    if (props.position === 'top') top += height + 6;
  }

  const header = document.querySelector('header');
  if(header && header.clientHeight) {
  if(top < header?.clientHeight) {
    top = header?.clientHeight;
  }}

  const menuWidth = props.width || (width);
  location.width = menuWidth;
  location.top = top;
  location.left = left - ((menuWidth - width) / 2);
  location.width = props.position === 'top' ? location.width + 2 : location.width;
}

function onClick() {
  open.value = !open.value;
}

function onClickItem() {
  if (props.closeOnSelect) open.value = !open.value;
}

const attrs = computed(() => ({
  onClick,
}));

watch(activatorRef, (newValue, oldValue) => {
  oldValue?.removeEventListener('click', onClick);
  newValue?.addEventListener('click', onClick);
}, { immediate: true });

useWindowResize(onResize);
useWindowScroll(onResize);


watch(()=> open.value, ()=> {
  if(open.value && props.hiddenScroll) {
    document.documentElement.style.overflow = 'hidden';
  }
  onResize();
  if(!open.value && props.hiddenScroll) {
    document.documentElement.style.overflow = 'auto';
  }
}, { immediate: true });


watch(() => props.modelValue, (newValue) => {
  open.value = newValue;
}, { immediate: true });

function observeHeight(element: HTMLElement) {
  const heightObserver = new ResizeObserver(() => {
    updateHeight();
  });
  heightObserver.observe(element);
}

onUnmounted(() => {
  activatorRef.value?.removeEventListener('click', onClick);
  document.documentElement.style.overflow = 'auto';
});

function updateHeight() {
  const currentHeight = menuRef.value?.offsetHeight || 0;
  if (menuHeight.value !== currentHeight) {
    menuHeight.value = currentHeight;
  }
}

watch(() => menuHeight.value, () => {
  onResize();
}, { immediate: true });

watch(() => menuRef.value, (newValue) => {
  if(newValue) {
    observeHeight(newValue);
  }
}, { immediate: true });

</script>

<style scoped>

.menu {
  position: absolute !important;
  display: inline-block;
  overflow-y: auto;
  overflow-x: hidden;
  contain: content;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.059);
  background-color: white;
}

.fade-enter-active {
  transition: opacity 0.4s ease-in;
}

.fade-leave-active {
  transition: opacity 0.2s ease-out;
}

</style>
